import { fetchApi } from "@/lib/api";
import { CLERK_AVAILABLE, getClerkAuthHeaders, getDemoAuthHeaders } from "@/lib/auth";
import { useAuthConditional } from "@/lib/use-auth-conditional";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// ============================================
// TYPES
// ============================================

export interface SubmissionLink {
  id: string;
  projectId: string;
  hash: string;
  name: string;
  description?: string | null;
  color?: string | null;
  isActive: boolean;
  expiresAt?: string | null;
  defaultCategory?: string | null;
  submissionCount: number;
  lastUsedAt?: string | null;
  createdBy: string;
  createdAt: string;
  url?: string; // Added by API
}

export interface JournalEntryPhoto {
  id: string;
  entryId: string;
  projectId: string;
  url: string;
  thumbnailUrl?: string | null;
  caption?: string | null;
  takenAt?: string | null;
  createdAt: string;
}

export interface JournalEntryUser {
  id: string;
  name: string;
  email: string;
}

export interface JournalEntry {
  id: string;
  projectId: string;
  entryType: "log" | "submission" | "note" | "checkpoint" | "update";
  source: "internal" | "link";
  sourceLinkId?: string | null;
  parentId?: string | null;
  createdBy?: string | null;
  submitterName?: string | null;
  submitterPhone?: string | null;
  submitterEmail?: string | null;
  title?: string | null;
  content: string;
  category?: string | null;
  weatherTemp?: number | null;
  weatherConditions?: string | null;
  crewCount?: number | null;
  locationLat?: number | null;
  locationLng?: number | null;
  hasIssue: boolean;
  isResolved: boolean;
  status: "active" | "archived";
  referenceNumber?: string | null;
  replyCount: number;
  metadata?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  // Relations
  sourceLink?: SubmissionLink | null;
  photos?: JournalEntryPhoto[];
  createdByUser?: JournalEntryUser | null;
}

export interface CreateEntryData {
  entryType?: "log" | "submission" | "note" | "checkpoint" | "update";
  title?: string;
  content: string;
  category?: string;
  weatherTemp?: number;
  weatherConditions?: string;
  crewCount?: number;
  hasIssue?: boolean;
  locationLat?: number;
  locationLng?: number;
  parentId?: string;
  metadata?: Record<string, unknown>;
}

export interface CreateSubmissionLinkData {
  name: string;
  description?: string;
  color?: string;
  defaultCategory?: string;
  expiresAt?: string;
}

export interface UpdateSubmissionLinkData {
  name?: string;
  description?: string | null;
  color?: string | null;
  defaultCategory?: string | null;
  isActive?: boolean;
  expiresAt?: string | null;
}

// ============================================
// SUBMISSION LINKS HOOKS
// ============================================

export function useSubmissionLinks(projectId: string) {
  const auth = useAuthConditional();

  return useQuery({
    queryKey: ["projects", projectId, "journal", "links"],
    queryFn: async () => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<{ links: SubmissionLink[] }>(
        `/api/projects/${projectId}/journal/links`,
        { headers }
      );
    },
    enabled: auth.isSignedIn && !!projectId,
    staleTime: 1000 * 60 * 5,
  });
}

export function useCreateSubmissionLink(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateSubmissionLinkData) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<{ link: SubmissionLink; url: string }>(
        `/api/projects/${projectId}/journal/links`,
        {
          method: "POST",
          headers,
          body: JSON.stringify(data),
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "journal", "links"] });
    },
  });
}

export function useUpdateSubmissionLink(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ linkId, data }: { linkId: string; data: UpdateSubmissionLinkData }) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<{ link: SubmissionLink; url: string }>(
        `/api/projects/${projectId}/journal/links/${linkId}`,
        {
          method: "PATCH",
          headers,
          body: JSON.stringify(data),
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "journal", "links"] });
    },
  });
}

export function useDeleteSubmissionLink(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (linkId: string) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<{ success: boolean }>(
        `/api/projects/${projectId}/journal/links/${linkId}`,
        {
          method: "DELETE",
          headers,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "journal", "links"] });
    },
  });
}

// ============================================
// JOURNAL ENTRIES HOOKS
// ============================================

export interface JournalFilters {
  type?: string;
  source?: string;
  linkId?: string;
  hasIssue?: boolean;
  category?: string;
  limit?: number;
  offset?: number;
}

export function useJournal(projectId: string, filters?: JournalFilters) {
  const auth = useAuthConditional();

  return useQuery({
    queryKey: ["projects", projectId, "journal", filters],
    queryFn: async () => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      const params = new URLSearchParams();
      if (filters?.type) params.set("type", filters.type);
      if (filters?.source) params.set("source", filters.source);
      if (filters?.linkId) params.set("linkId", filters.linkId);
      if (filters?.hasIssue) params.set("hasIssue", "true");
      if (filters?.category) params.set("category", filters.category);
      if (filters?.limit) params.set("limit", filters.limit.toString());
      if (filters?.offset) params.set("offset", filters.offset.toString());

      const queryString = params.toString();
      const url = `/api/projects/${projectId}/journal${queryString ? `?${queryString}` : ""}`;

      return fetchApi<{
        entries: JournalEntry[];
        links: SubmissionLink[];
        pagination: {
          total: number;
          limit: number;
          offset: number;
          hasMore: boolean;
        };
      }>(url, { headers });
    },
    enabled: auth.isSignedIn && !!projectId,
    staleTime: 1000 * 60 * 2,
  });
}

export function useJournalEntry(projectId: string, entryId: string) {
  const auth = useAuthConditional();

  return useQuery({
    queryKey: ["projects", projectId, "journal", entryId],
    queryFn: async () => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<{ entry: JournalEntry; replies: JournalEntry[] }>(
        `/api/projects/${projectId}/journal/${entryId}`,
        { headers }
      );
    },
    enabled: auth.isSignedIn && !!projectId && !!entryId,
    staleTime: 1000 * 60 * 2,
  });
}

export function useCreateJournalEntry(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateEntryData) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<{ entry: JournalEntry }>(
        `/api/projects/${projectId}/journal`,
        {
          method: "POST",
          headers,
          body: JSON.stringify(data),
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "journal"] });
    },
  });
}

export function useCreateReply(projectId: string, entryId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (content: string) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<{ reply: JournalEntry }>(
        `/api/projects/${projectId}/journal/${entryId}/reply`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({ content }),
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "journal"] });
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "journal", entryId] });
    },
  });
}

export function useUpdateJournalEntry(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      entryId,
      data,
    }: {
      entryId: string;
      data: { isResolved?: boolean; hasIssue?: boolean; status?: "archived" };
    }) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<{ entry: JournalEntry }>(
        `/api/projects/${projectId}/journal/${entryId}`,
        {
          method: "PATCH",
          headers,
          body: JSON.stringify(data),
        }
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "journal"] });
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "journal", variables.entryId],
      });
    },
  });
}
